import {
  useQuery,
  UseQueryResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import getSlackChannels, {
  SlackChannel,
  GetSlackChannelsResponse,
} from '@/apis/getSlackChannels';
import getSlackUsers, { GetSlackUsersResponse } from '@/apis/getSlackUsers';
import updateSlackUserMapping from '@/apis/updateSlackUserMapping';
import getDmNotificationStatus, {
  GetDmNotificationStatusResponse,
} from '@/apis/getDmNotificationStatus';
import updateDmNotificationStatus from '@/apis/updateDmNotificationStatus';
import { getAllUsers } from '@/apis/ApiUtil';
import isSlackConfigured from '@/apis/isSlackConfigured';
import headerObject from '@/types/headerObj';
import User from '@/types/userType';
import { toast } from '@/utils/toastManager';
import useOrganizationStore from '@/state/orgState';
import useUserStore from '@/state/userState';

export interface SlackData {
  isConfigured: boolean;
  workspaceName: string | null;
  workspaceDomain: string | null; // Subdomain for workspace URL
  workspaceIcon: string | null; // team.info icon URL; null => render branded fallback
  channels: SlackChannel[];
}

// Hook for checking if Slack is configured
export const useSlackConfigured = (
  organizationUUID?: string | undefined
): UseQueryResult<boolean> => {
  const { organization } = useOrganizationStore();
  const { user } = useUserStore();

  const headerObj: headerObject = {
    Orguuid: organization?.OrgUUID || '',
    Useruuid: user?.UserUUID || '',
    Useremail: user?.Email || '',
    Username: user?.FirstName || '',
    Orgname: organization?.OrgName || '',
  };

  return useQuery<boolean>({
    queryKey: [
      'slack',
      'configured',
      organizationUUID || organization?.OrgUUID,
    ],
    queryFn: async () => {
      const orgUUID = organizationUUID || organization?.OrgUUID;
      if (!orgUUID) return false;
      const configured = await isSlackConfigured(
        { OrgUUID: orgUUID },
        headerObj
      );
      return configured || false;
    },
    enabled: !!(organizationUUID || organization?.OrgUUID),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true, // Refetch when user returns from OAuth
  });
};

// Hook for fetching Slack channels
export const useSlackChannels = (
  organizationUUID: string | undefined,
  isConfigured: boolean
): UseQueryResult<GetSlackChannelsResponse | null> => {
  const { organization } = useOrganizationStore();
  const { user } = useUserStore();

  const headerObj: headerObject = {
    Orguuid: organization?.OrgUUID || '',
    Useruuid: user?.UserUUID || '',
    Useremail: user?.Email || '',
    Username: user?.FirstName || '',
    Orgname: organization?.OrgName || '',
  };

  return useQuery<GetSlackChannelsResponse | null>({
    queryKey: ['slack', 'channels', organizationUUID || organization?.OrgUUID],
    queryFn: async () => {
      const orgUUID = organizationUUID || organization?.OrgUUID;
      if (!orgUUID) return null;
      const response = await getSlackChannels({ OrgUUID: orgUUID }, headerObj);
      return response;
    },
    enabled: !!(organizationUUID || organization?.OrgUUID) && isConfigured,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true, // Refetch when user returns from OAuth
  });
};

// Combined hook for all Slack data
export const useSlackData = (
  organizationUUID: string | undefined
): {
  configured: UseQueryResult<boolean>;
  channels: UseQueryResult<GetSlackChannelsResponse | null>;
  isLoading: boolean;
  error: Error | null;
  data: SlackData;
} => {
  const configured = useSlackConfigured(organizationUUID);
  const channels = useSlackChannels(organizationUUID, configured.data || false);

  const slackData: SlackData = {
    isConfigured: configured.data || false,
    workspaceName: channels.data?.WorkspaceName || null,
    workspaceDomain: channels.data?.WorkspaceDomain || null,
    workspaceIcon: channels.data?.WorkspaceIcon || null,
    channels: channels.data?.Channels || [],
  };

  return {
    configured,
    channels,
    isLoading:
      configured.isLoading || (!!configured.data && channels.isLoading),
    error: configured.error || channels.error,
    data: slackData,
  };
};

// Hook for fetching Slack users
export const useSlackUsers = (
  organizationUUID: string | undefined,
  isConfigured: boolean
): UseQueryResult<GetSlackUsersResponse | null> => {
  const { organization } = useOrganizationStore();
  const { user } = useUserStore();

  const headerObj: headerObject = {
    Orguuid: organization?.OrgUUID || '',
    Useruuid: user?.UserUUID || '',
    Useremail: user?.Email || '',
    Username: user?.FirstName || '',
    Orgname: organization?.OrgName || '',
  };

  return useQuery<GetSlackUsersResponse | null>({
    queryKey: ['slack', 'users', organizationUUID || organization?.OrgUUID],
    queryFn: async () => {
      const orgUUID = organizationUUID || organization?.OrgUUID;
      if (!orgUUID) return null;
      return await getSlackUsers({ OrgUUID: orgUUID });
    },
    enabled: !!(organizationUUID || organization?.OrgUUID) && isConfigured,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook for fetching all organization users for GitHub/GitLab username dropdowns
export const useAllUsersForMapping = (
  organizationUUID: string | undefined
): UseQueryResult<User[]> => {
  const { organization } = useOrganizationStore();
  const { user } = useUserStore();

  const headerObj: headerObject = {
    Orguuid: organization?.OrgUUID || '',
    Useruuid: user?.UserUUID || '',
    Useremail: user?.Email || '',
    Username: user?.FirstName || '',
    Orgname: organization?.OrgName || '',
  };

  return useQuery<User[]>({
    queryKey: ['allUsers', organizationUUID || organization?.OrgUUID],
    queryFn: async () => {
      const orgUUID = organizationUUID || organization?.OrgUUID;
      if (!orgUUID) return [];
      const data = await getAllUsers(orgUUID, headerObj);
      return data?.Users || [];
    },
    enabled: !!(organizationUUID || organization?.OrgUUID),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook for updating Slack user mapping
export const useUpdateSlackUserMapping = (
  organizationUUID: string | undefined
) => {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { user } = useUserStore();

  const headerObj: headerObject = {
    Orguuid: organization?.OrgUUID || '',
    Useruuid: user?.UserUUID || '',
    Useremail: user?.Email || '',
    Username: user?.FirstName || '',
    Orgname: organization?.OrgName || '',
  };

  return useMutation({
    mutationFn: async (data: {
      slackUserId: string;
      githubUsername?: string | null;
      gitlabUsername?: string | null;
    }) => {
      const orgUUID = organizationUUID || organization?.OrgUUID;
      if (!orgUUID) return null;
      return await updateSlackUserMapping(
        {
          OrgUUID: orgUUID,
          SlackUserId: data.slackUserId,
          GithubUsername: data.githubUsername,
          GitlabUsername: data.gitlabUsername,
        },
        headerObj
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['slack', 'users', organizationUUID || organization?.OrgUUID],
      });
      toast.show({
        title: 'Success',
        description: 'Slack user mapping updated successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast.show({
        title: 'Error',
        description: error?.message || 'Failed to update Slack user mapping',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });
};

// Hook for getting DM notification status
export const useDmNotificationStatus = (
  organizationUUID: string | undefined,
  isConfigured: boolean
): UseQueryResult<GetDmNotificationStatusResponse | null> => {
  const { organization } = useOrganizationStore();
  const { user } = useUserStore();

  const headerObj: headerObject = {
    Orguuid: organization?.OrgUUID || '',
    Useruuid: user?.UserUUID || '',
    Useremail: user?.Email || '',
    Username: user?.FirstName || '',
    Orgname: organization?.OrgName || '',
  };

  return useQuery<GetDmNotificationStatusResponse | null>({
    queryKey: [
      'slack',
      'dmNotificationStatus',
      organizationUUID || organization?.OrgUUID,
    ],
    queryFn: async () => {
      const orgUUID = organizationUUID || organization?.OrgUUID;
      if (!orgUUID) return null;
      return await getDmNotificationStatus({ OrgUUID: orgUUID }, headerObj);
    },
    enabled: !!(organizationUUID || organization?.OrgUUID) && isConfigured,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook for updating DM notification status
export const useUpdateDmNotificationStatus = (
  organizationUUID: string | undefined
) => {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { user } = useUserStore();

  const headerObj: headerObject = {
    Orguuid: organization?.OrgUUID || '',
    Useruuid: user?.UserUUID || '',
    Useremail: user?.Email || '',
    Username: user?.FirstName || '',
    Orgname: organization?.OrgName || '',
  };

  return useMutation({
    mutationFn: async (dmEnabled: boolean) => {
      const orgUUID = organizationUUID || organization?.OrgUUID;
      if (!orgUUID) return null;
      return await updateDmNotificationStatus(
        {
          OrgUUID: orgUUID,
          DmEnabled: dmEnabled,
        },
        headerObj
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          'slack',
          'dmNotificationStatus',
          organizationUUID || organization?.OrgUUID,
        ],
      });
      toast.show({
        title: 'Success',
        description: 'DM notification settings updated successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast.show({
        title: 'Error',
        description:
          error?.message || 'Failed to update DM notification settings',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });
};
